import React, { useState, useEffect, useCallback } from 'react';
import { Search, Upload, Eye, BarChart3, Loader2, ChevronRight, Package, Layers, Filter, Calculator, Box, Plus, Trash2, Download } from 'lucide-react';
import { bomAPI, fetchPaged, masterChangeAPI } from '../../services/api';
import Pagination from '../ui/Pagination';
import { toast } from 'sonner';
import BOMImportWizard from './BOMImportWizard';
import BOMDetailView from './BOMDetailView';
import BOMCreateForm from './BOMCreateForm';

const categoryLabels = {
  SIGF: 'Signage Facia', SIGS: 'Signage Sticker', SIGN: 'General Signage',
  TRM1: 'Thermoplastic', ECO1: 'Ecolite', GANT: 'Gantry', DEL0: 'Delineator',
  FGPR: 'Premium FG', PRIM: 'Primer', STIC: 'Sticker', SKU0: 'SKU',
};

const BOMModule = () => {
  const [view, setView] = useState('list');
  const [boms, setBoms] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, categories: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activePrefix, setActivePrefix] = useState('');
  const [selectedBomId, setSelectedBomId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [exporting, setExporting] = useState(false);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (search) params.search = search;
      if (activePrefix) params.fg_prefix = activePrefix;
      await bomAPI.exportExcel(params);
      toast.success('BOMs exported to Excel');
    } catch (err) {
      toast.error('Failed to export BOMs');
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (activePrefix) params.fg_prefix = activePrefix;
      const [result, bomStats] = await Promise.all([
        fetchPaged('/boms', params),
        bomAPI.getStats()
      ]);
      setBoms(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(bomStats);
    } catch (err) {
      console.error('Failed to load BOMs:', err);
    } finally {
      setLoading(false);
    }
  }, [search, activePrefix, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openBOM = (bomId) => { setSelectedBomId(bomId); setView('detail'); };

  if (view === 'import') {
    return <BOMImportWizard onBack={() => { setView('list'); fetchData(); }} />;
  }

  if (view === 'create') {
    return <BOMCreateForm onBack={() => setView('list')} onCreated={() => { setView('list'); fetchData(); }} />;
  }

  if (view === 'detail' && selectedBomId) {
    return <BOMDetailView bomId={selectedBomId} onBack={() => { setView('list'); setSelectedBomId(null); }} />;
  }

  const sortedCategories = Object.entries(stats.categories).sort((a, b) => b[1] - a[1]);

  const handleDeleteBOM = async (bom) => {
    const reason = window.prompt(`Reason for deleting BOM "${bom.bom_name}":`);
    if (reason === null) return;
    try {
      await masterChangeAPI.create({ entity_type: 'bom', entity_id: bom.id, entity_name: `${bom.bom_number} - ${bom.bom_name}`, action: 'delete', reason });
      toast.success('Delete request submitted for Admin approval');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  return (
    <div className="p-5 lg:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" data-testid="bom-title" style={{ fontFamily: 'DM Sans' }}>Bill of Materials</h1>
          <p className="text-xs text-gray-500 mt-0.5">Manage production BOMs, materials, and cost estimation</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView('create')} data-testid="create-bom-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
            <Plus className="w-3.5 h-3.5" /> Create BOM
          </button>
          <button onClick={() => setView('import')} data-testid="import-bom-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
            <Upload className="w-3.5 h-3.5" /> Import BOMs
          </button>
          <button onClick={handleExport} disabled={exporting} data-testid="export-bom-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export Excel
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total BOMs', value: stats.total, icon: Layers, color: '#24549c' },
          { label: 'Active', value: stats.active, icon: Box, color: '#059669' },
          { label: 'Categories', value: sortedCategories.length, icon: Filter, color: '#7c3aed' },
          { label: 'Draft', value: stats.draft, icon: Package, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
              <span className="text-[11px] font-medium text-gray-500">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Category Pills */}
      {sortedCategories.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <button onClick={() => { setActivePrefix(''); setPage(1); }} data-testid="filter-all"
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${!activePrefix ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            style={!activePrefix ? { backgroundColor: '#24549c' } : {}}>
            All ({stats.total})
          </button>
          {sortedCategories.map(([prefix, count]) => (
            <button key={prefix} onClick={() => { setActivePrefix(activePrefix === prefix ? '' : prefix); setPage(1); }}
              data-testid={`filter-${prefix}`}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${activePrefix === prefix ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              style={activePrefix === prefix ? { backgroundColor: '#24549c' } : {}}>
              {categoryLabels[prefix] || prefix} ({count})
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search BOM number, name, or FG item..."
          data-testid="bom-search"
          className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
      </div>

      {/* BOM List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : boms.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-3">No BOMs found</p>
          <button onClick={() => setView('import')} className="px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
            Import from Excel
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 font-semibold text-gray-500">BOM #</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Name</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">FG Item</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Items</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-500">FG Cost (Rs.)</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Ver</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {boms.map(bom => (
                <tr key={bom.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`bom-row-${bom.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">{bom.bom_number}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-[250px] truncate">{bom.bom_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700">{bom.fg_item_id}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">{bom.items?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">
                    {bom.total_fg_cost ? `${Number(bom.total_fg_cost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center"><span className="text-[10px] font-semibold text-gray-400">v{bom.version}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openBOM(bom.id)} data-testid={`view-bom-${bom.id}`}
                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="View BOM">
                        <Eye className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
                      </button>
                      <button onClick={() => openBOM(bom.id)} title="Cost Simulator"
                        className="p-1.5 rounded-lg hover:bg-purple-50 transition-colors">
                        <Calculator className="w-3.5 h-3.5 text-purple-600" />
                      </button>
                      <button onClick={() => handleDeleteBOM(bom)} data-testid={`delete-bom-${bom.id}`}
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Request Delete">
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {boms.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
    </div>
  );
};

export default BOMModule;
